import React, { useReducer, useEffect, useState } from 'react';
/**
 * `DealerJourneyManager` orchestrates the dealer journey.
 *
 * Responsibilities:
 * - Owns `JourneyState` via `useReducer(JourneyReducer, initialJourneyState)`.
 * - Exposes a `JourneyContext` to child pages via the router `Outlet`.
 * - Filters product documents on route changes using `filterProductDocuments`.
 * - Registers policies via `PolicyApi.register` and updates reducer state with
 *   order numbers / failures.
 * - Renders shared layout (nav bar, stepper, page info side panels, reload
 *   guard).
 */
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import '../common/VehiclePage/VehiclePage.scss';
import {
  ProductType,
  ProductVariant,
  VehicleState,
  PersonalDetailType,
  AddressDetails,
  DirectDebitDetails,
  PolicyServer,
  PolicyResponse,
  DocumentTreeItem,
  JourneyContext,
  QuoteProduct,
  RegisterPolicyDto,
  JourneyState
} from '../common/CommonTypes';
import JourneyReducer from '../../journeyState/JourneyReducer';
import NavBar from '../common/NavBar';
import {
  filterProductDocuments,
  getSelectedProducts,
  getSelectedVariant
} from '../common/ProductMethods';
import {
  PageAbsolutePath,
  RegistrationState,
  initialJourneyState,
  functionNotImplemented,
  PageRelativePath
} from '../../consts/Common';
import PageInfo from './PageInfo';
import NavStepper, { GetNavDispatch } from '../common/NavStepper';
import MobileInfoBar from '../common/MobileInfoBar';
import { useCurrentPath } from '../sitePages/useCurrentPath';
import { PolicyApi } from '../../api/PolicyApi';
import { getPolicyServer } from './PolicyMethods';
import ReloadHandler from '../common/ReloadHandler';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../app-state/Hooks';
import { checkToken } from '../common/HelperMethods';

const DealerJourneyManager = (): JSX.Element => {
  const { state } = useLocation();

  const initialDealerJourneyState = { ...initialJourneyState };

  if (state?.journeyState) {
    Object.assign(initialDealerJourneyState, state.journeyState);
  }

  const [journey, dispatch] = useReducer(
    JourneyReducer,
    initialDealerJourneyState
  );

  const navigate = useNavigate();
  const currentPath = useCurrentPath();
  const { t } = useTranslation();

  const [updateDocuments, setUpdateDocuments] = useState(false);

  const {
    products,
    personalDetail,
    vehicle,
    directDebitDetails,
    navigationStatus,
    registrationFailMsg,
    registrationState,
    renewalMarketing
  } = journey;

  useEffect(() => {
    if (
      CommonValidation.isNotEmpty(registrationFailMsg) &&
      registrationState === RegistrationState.Failed
    ) {
      navigate(PageRelativePath.DealerFailedRegistration, {
        relative: 'path'
      });
    }
  }, [registrationFailMsg]);

  useEffect(() => {
    setProductDocuments(
      journey.products,
      journey.vehicle.vehicleGapIntroductionDate,
      journey.documents,
      currentPath
    );

    checkToken(userToken, userTokenExpiresOn, dispatch);
  }, [currentPath]);

  useEffect(() => {
    if (updateDocuments) {
      setProductDocuments(
        journey.products,
        journey.vehicle.vehicleGapIntroductionDate,
        journey.documents,
        currentPath
      );
    }
  }, [updateDocuments]);

  const setProductDocuments = (
    initialProducts: ProductType[],
    gapIntroductionDate: string | undefined,
    documents: DocumentTreeItem[],
    path: string
  ): void => {
    setUpdateDocuments(false);

    const newDocuments = filterProductDocuments(
      initialProducts,
      gapIntroductionDate,
      documents,
      path
    );

    if (newDocuments) {
      onUpdateProductDocuments(newDocuments);
    }
  };

  const setExpandedDocIds = (docIds: string[]) => {
    dispatch({
      type: 'set-expanded-node-ids',
      expandedDocIds: docIds
    });
  };

  const onUpdateVehicleDetails = (vehicleState: VehicleState) => {
    dispatch({
      type: 'set-vehicle',
      vehicle: vehicleState
    });
  };

  const onChangeMileage = (currentMileage: number | null) => {
    dispatch({
      type: 'set-current-mileage',
      currentMileage: currentMileage
    });
  };

  const onChangeVehiclePurchasePrice = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const value = (e.target as HTMLInputElement).value;

    if (CommonValidation.isEmpty(value) || isNaN(parseInt(value))) {
      dispatch({
        type: 'set-vehicle-purchase-price',
        vehiclePurchasePrice: 0
      });
    } else {
      dispatch({
        type: 'set-vehicle-purchase-price',
        vehiclePurchasePrice: parseInt(value)
      });
    }
  };

  const onChangeVehicleDeliveryDate = (vehicleDeliveryDate: string) => {
    dispatch({
      type: 'set-vehicle-delivery-date',
      vehicleDeliveryDate: vehicleDeliveryDate
    });
  };

  const onChangeVehicleGapIntroductionDate = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    dispatch({
      type: 'set-vehicle-gap-introduction-date',
      vehicleGapIntroductionDate: (e.target as HTMLInputElement).value
    });
  };

  const onChangeSalesId = (salesId: string) => {
    dispatch({
      type: 'set-sales-id',
      salesId: salesId
    });
  };

  const onUpdateProducts = (
    initialProducts: ProductType[],
    updateDocs?: boolean
  ) => {
    dispatch({
      type: 'set-products',
      products: initialProducts
    });
    if (updateDocs === true) {
      setUpdateDocuments(true);
    }
  };

  const onUpdateQuoteProducts = (quoteProducts: QuoteProduct[]) => {
    dispatch({
      type: 'set-quote-products',
      quoteProducts
    });
  };

  const onUpdateProductDocuments = (documents: DocumentTreeItem[]) => {
    dispatch({
      type: 'set-documents',
      documents
    });
  };

  const mapVariants = (
    variants: ProductVariant[],
    selectedVariantId: string
  ): ProductVariant[] => {
    const updatedVariants = variants.map((v) => ({
      ...v,
      isSelected: v.variantId === selectedVariantId
    }));

    return updatedVariants;
  };

  const onUpdateProductVariant = (productId: number, variantId: string) => {
    const updatedProducts = journey.products.map((p) =>
      p.productTypeId === productId
        ? { ...p, productVariants: mapVariants(p.productVariants, variantId) }
        : { ...p }
    );

    dispatch({
      type: 'set-products',
      products: updatedProducts
    });
  };

  const setPersonalDetails = (personalDetailType: PersonalDetailType) => {
    dispatch({
      type: 'set-personal-details',
      personalDetail: personalDetailType
    });
  };

  const setAddressSinglePayment = (addressDetails: AddressDetails) => {
    dispatch({
      type: 'set-address-single-payment',
      addressDetails: addressDetails
    });
  };

  const setDirectDebitDetails = (directDebit: DirectDebitDetails) => {
    dispatch({
      type: 'set-direct-debit-details',
      directDebitDetails: directDebit
    });
  };

  const onUpdateAgreedTermsAndConditions = (hasAgreed: boolean) => {
    dispatch({
      type: 'set-agreed-terms-and-conditions',
      hasAgreed
    });
  };

  const onProductDocumentsDownloaded = (downloaded: boolean) => {
    dispatch({
      type: 'set-agreed-terms-and-conditions',
      hasAgreed: downloaded
    });
  };

  const onUpdateRenewalMarketing = (consentFlag: boolean) => {
    dispatch({
      type: 'set-consent-to-renewal-marketing',
      renewalMarketing: consentFlag
    });
  };

  const onSetJourney = (journeyState: JourneyState) => {
    dispatch({
      type: 'set-journey-state',
      journeyState
    });
  };

  const onUpdateFoundExistingQuotes = (foundExistingQuotes: boolean) => {
    dispatch({
      type: 'set-included-quotes-data',
      foundExistingQuotes
    });
  };

  const dealerCode = useAppSelector((s) => s.user.dealerCode);
  const userToken = useAppSelector((s) => s.user.token);
  const userTokenExpiresOn = useAppSelector((s) => s.user.tokenExpiresOn);
  const userInstanceId = useAppSelector((s) => s.user.instanceId);
  const journeyType = useAppSelector((s) => s.appConfig.journeyType);
  const brandWorldPay = useAppSelector((s) => s.brand.worldPay);
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);

  const register = (cardTransactionId: string) => {
    const controller = new AbortController();

    const selectedProducts = getSelectedProducts(products);

    const uniquePaymentMethodSelected: string[] = [];
    const uniqueInstallmentsSelected: number[] = [];

    selectedProducts.forEach((p) => {
      const selectedVariant = getSelectedVariant(p.productVariants);

      const paymentMethod = selectedVariant?.paymentMethod;

      if (paymentMethod && paymentMethod.toLowerCase() == 'monthly') {
        const noOfInstallments = selectedVariant.noOfInstallments;

        if (
          noOfInstallments &&
          !uniqueInstallmentsSelected.find((i) => i == noOfInstallments)
        ) {
          uniqueInstallmentsSelected.push(noOfInstallments);
        }
      }

      if (
        paymentMethod &&
        !uniquePaymentMethodSelected.find((pm) => pm == paymentMethod)
      ) {
        uniquePaymentMethodSelected.push(paymentMethod);
      }
    });

    const policy: PolicyServer[] = [];

    uniquePaymentMethodSelected.forEach((pm) => {
      if (pm.toLowerCase() == 'monthly') {
        uniqueInstallmentsSelected.forEach((i) => {
          const policyDetail = getPolicy(pm, cardTransactionId, i);

          if (policyDetail) {
            policy.push(policyDetail);
          }
        });
      } else {
        const policyDetail = getPolicy(pm, cardTransactionId, 0);

        if (policyDetail) {
          policy.push(policyDetail);
        }
      }
    });

    const successCallback = (policyResponse: PolicyResponse[]) => {
      const orderNumbers = policyResponse.map((pr) => pr.policyNumber);

      dispatch({
        type: 'set-order-numbers',
        orderNumbers: orderNumbers,
        authorisationCode: ''
      });
    };

    const failureCallback = () => {
      if (cardTransactionId) {
        setRegistrationState(RegistrationState.VoidingPayment);
      } else {
        setRegistrationFailMsg(t('common-error-msg.submission-failed-message'));
      }
    };

    const registerPolicyDto: RegisterPolicyDto = {
      policy: policy,
      allowPastPurchaseDate: false,
      countryCode: countryCode
    };

    setRegistrationState(RegistrationState.Registering);

    new PolicyApi().register(
      registerPolicyDto,
      userToken,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const getPolicy = (
    paymentMethod: string,
    cardTransactionId: string,
    noOfInstallments: number
  ): PolicyServer | undefined => {
    const policyStartDate = '';
    const isLateRenewal = false;

    return getPolicyServer(
      paymentMethod,
      cardTransactionId,
      products,
      vehicle,
      personalDetail,
      directDebitDetails,
      policyStartDate,
      isLateRenewal,
      dealerCode,
      userInstanceId,
      vehicle.salesId ?? vehicle.dealerReference,
      brandWorldPay,
      paymentMethod.toLowerCase() == 'monthly' ? noOfInstallments : undefined,
      renewalMarketing,
      journeyType
    );
  };

  const onPaymentVoided = () => {
    dispatch({
      type: 'set-registration-fail-msg',
      message: t('common-error-msg.payment-voided-message')
    });
  };

  const onPaymentFailure = () => {
    dispatch({
      type: 'set-registration-fail-msg',
      message: 'Failed to make the payment.'
    });
  };

  const setRegistrationFailMsg = (message: string) => {
    dispatch({
      type: 'set-registration-fail-msg',
      message: message
    });
  };

  const setRegistrationState = (regState: number) => {
    dispatch({
      type: 'set-registration-state',
      regState: regState
    });
  };

  const setQuoteId = (quoteId: number) => {
    dispatch({
      type: 'set-quote-id',
      quoteId: quoteId
    });
  };

  const setHasQuoteExpired = (hasQuoteExpired: boolean) => {
    dispatch({
      type: 'set-quote-expired',
      hasQuoteExpired: hasQuoteExpired
    });
  };

  const setIsExpiredMessageAcknowledged = (
    isExpiredMessageAcknowledged: boolean
  ) => {
    dispatch({
      type: 'set-message-acknowledged',
      isExpiredMessageAcknowledged: isExpiredMessageAcknowledged
    });
  };

  const onUpdateVehicleVrn = (vrn: string) => {
    dispatch({
      type: 'set-vehicle-vrn',
      vrn: vrn
    });
  };

  const onPageChange = (page: string, status: boolean) => {
    const action = GetNavDispatch(page, status);

    if (action !== undefined) {
      dispatch(action);
    }
  };

  const onUpdateQuotedByDealerReference = (dealerReference: string) => {
    dispatch({
      type: 'set-quoted-by-dealer',
      quotedByDealerReference: dealerReference
    });
  };

  const onUpdateManufacturingCoverageMonths = (months: number) => {
    dispatch({
      type: 'set-manufacturing-coverage-period-months',
      manufacturingCoverageMonths: months
    });
  };

  const dealerJourneyContext: JourneyContext = {
    journey: journey,
    onUpdateVehicleDetails: onUpdateVehicleDetails,
    onChangeMileage: onChangeMileage,
    onChangeVehiclePurchasePrice: onChangeVehiclePurchasePrice,
    onChangeVehicleDeliveryDate: onChangeVehicleDeliveryDate,
    onChangeVehicleGapIntroductionDate: onChangeVehicleGapIntroductionDate,
    onChangeSalesId: onChangeSalesId,
    onUpdateProducts: onUpdateProducts,
    onUpdateQuotedProducts: onUpdateQuoteProducts,
    onUpdateProductDocuments: onUpdateProductDocuments,
    onUpdateProductVariant: onUpdateProductVariant,
    setPersonalDetails: setPersonalDetails,
    setAddressSinglePayment: setAddressSinglePayment,
    setDirectDebitDetails: setDirectDebitDetails,
    onUpdateAgreedTermsAndConditions: onUpdateAgreedTermsAndConditions,
    onRegister: register,
    onPaymentVoided: onPaymentVoided,
    onPaymentFailure: onPaymentFailure,
    onPageChange: onPageChange,
    getPolicy: getPolicy,
    setRegistrationFailMsg: setRegistrationFailMsg,
    setExpandedDocIds: setExpandedDocIds,
    onUpdateFoundExistingQuotes: onUpdateFoundExistingQuotes,
    onPolicyStartDateChange: functionNotImplemented,
    setQuoteId: setQuoteId,
    setHasQuoteExpired: setHasQuoteExpired,
    onUpdateVehicleVrn: onUpdateVehicleVrn,
    setIsExpiredMessageAcknowledged: setIsExpiredMessageAcknowledged,
    onUpdateQuotedByDealerReference: onUpdateQuotedByDealerReference,
    onUpdateRenewalMarketing: onUpdateRenewalMarketing,
    onSetJourney: onSetJourney,
    onProductDocumentsDownloaded: onProductDocumentsDownloaded,
    onUpdateManufacturingCoverageMonths: onUpdateManufacturingCoverageMonths
  };

  const isAllowedToRenderMIB = () => {
    const notAllowed: string[] = [
      PageAbsolutePath.Confirmation,
      PageAbsolutePath.FailedRegistration
    ];
    return !notAllowed.includes(currentPath);
  };

  return (
    <>
      <div className='flex-column main-column'>
        <div className='main-content'>
          <NavBar />
          <NavStepper
            products={products}
            navigationStatus={navigationStatus}
            currentPath={currentPath}
          />
          <div className='page-container'>
            <div className='page-header-line' />
            <Outlet context={dealerJourneyContext} />
            {isAllowedToRenderMIB() && (
              <MobileInfoBar
                products={journey.products}
                documents={journey.documents}
                expandedDocIds={journey.expandedDocIds}
                currentPath={currentPath}
                setExpandedDocIds={setExpandedDocIds}
              />
            )}
          </div>
        </div>
      </div>
      <div className='flex-column desktop-column'>
        <PageInfo
          products={journey.products}
          documents={journey.documents}
          expandedDocIds={journey.expandedDocIds}
          currentPath={currentPath}
          setExpandedDocIds={setExpandedDocIds}
        />
      </div>
      <ReloadHandler reloadPath={PageAbsolutePath.Landing} />
    </>
  );
};

export default DealerJourneyManager;
